import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
// jl-design-system
import Input from 'jl-design-system/elements/input/Input';
import Textarea from 'jl-design-system/elements/textarea/Textarea';
// utils
import {
  getTextAreaLinesArray,
  isAscii,
  getNextCharsInfo,
  getCharsLeft,
  calcLinesAfterEdit,
  replaceNonAsciiExtended,
  parseCharCode,
} from '../../utils/string/textAreaUtils';

import styles from './gift-message.scss';

export class GiftMessage extends PureComponent {

  static propTypes = {
    ...Input.propTypes,
    ariaDescribedBy: PropTypes.string,
    describedById: PropTypes.string,
    maxChars: PropTypes.number,
    maxCharsLine: PropTypes.number,
    maxLines: PropTypes.number,
    value: PropTypes.string,
  };

  static defaultProps = {
    ...Input.defaultProps,
    ariaDescribedBy: undefined,
    describedById: undefined,
    maxChars: 0,
    maxCharsLine: 0,
    maxLines: 0,
    value: '',
  };

  constructor(props) {
    super(props);

    this.textArea = {
      input: {},
    };

    const { value, maxLines, maxChars, maxCharsLine } = this.props;
    const linesArray = getTextAreaLinesArray({
      string: value,
      maxLines,
      maxCharsLine,
    });
    const charsLeft = getCharsLeft({
      textArea: this.textArea.input,
      linesArray,
      maxChars,
      maxLines,
      maxCharsLine,
    });

    this.state = {
      charsLeft,
      linesLeft: maxLines - linesArray.length,
    };
  }

  onKeyDown = (evt) => {
    const { maxLines, maxCharsLine } = this.props;
    const { charsLeft } = this.state;
    const charCode = evt.key.toLowerCase();

    if (charCode.indexOf('arrow') === -1) {

      const isBackspace = charCode === 'backspace' || charCode === 'delete';
      const isTab = charCode === 'tab';
      const { prevChar, nextChar, newLine } = getNextCharsInfo(this.textArea.input);
      const doNotAllowSpace = prevChar === ' ' || nextChar === ' ' || newLine;
      const isNotAscii = !isAscii({ value: charCode, extended: true });
      const insert = parseCharCode(charCode);

      const linesAfterEdit = calcLinesAfterEdit({
        textArea: this.textArea.input,
        insert,
        maxCharsLine,
      });
      const willExceedMaxLines = linesAfterEdit > maxLines;

      if (
        (!charsLeft && !isBackspace && !isTab) ||
        isNotAscii ||
        (charCode === ' ' && doNotAllowSpace) ||
        willExceedMaxLines
      ) {
        evt.preventDefault();
      }
    }
  };

  onKeyUp = (evt) => {
    const charCode = evt.key.toLowerCase();

    if (charCode.indexOf('arrow') !== -1) {
      this.calcStats();
    }
  };

  onChange = (evt) => {
    this.textArea.input.value = replaceNonAsciiExtended(this.textArea.input.value);

    // pass event through to redux-form
    const { onChange } = this.props;
    onChange(evt);

    this.calcStats();
  };

  onFocus = (evt) => {
    // pass event through to redux-form
    const { onFocus } = this.props;
    onFocus(evt);

    this.calcStats();
  };

  onBlur = (evt) => {
    // pass event through to redux-form
    const { onBlur } = this.props;
    onBlur(evt);

    this.calcStats();
  };

  onPaste = (evt) => {
    const { maxLines, maxCharsLine } = this.props;
    const clipboardData = replaceNonAsciiExtended(evt.clipboardData.getData('Text'));
    const willExceedMaxLines = calcLinesAfterEdit({
      textArea: this.textArea.input,
      insert: clipboardData,
      maxCharsLine,
    }) > maxLines;

    evt.clipboardData.setData('Text', clipboardData);

    if (willExceedMaxLines) {
      evt.preventDefault();
    }
  };

  getRemainingCharacters = (linesArray) => {
    const { maxChars, maxCharsLine, maxLines } = this.props;

    return getCharsLeft({
      textArea: this.textArea.input,
      linesArray,
      maxLines,
      maxChars,
      maxCharsLine,
    });
  }

  calcStats() {
    const { maxCharsLine, maxLines } = this.props;

    const { value } = this.textArea.input;

    const linesArray = getTextAreaLinesArray({
      string: value,
      maxLines,
      maxCharsLine,
    });

    let charsLeft = this.getRemainingCharacters(linesArray);

    // Handle negative values because of iOS https://www.jlpit.com/jira/browse/MARV-4403
    const charsLeftIsNegative = charsLeft && charsLeft / Math.abs(charsLeft) < 0;
    if (charsLeftIsNegative) {
      // trim the characters that have gone over the limit and set the textarea value
      this.textArea.input.value =
        this.textArea.input.value.substring(Math.abs(charsLeft), this.textArea.input.value.length).trim();

      // recalculate new characters left after truncating
      charsLeft = this.getRemainingCharacters(linesArray);
    }

    const linesLeftIsNegative = maxLines - linesArray.length < 0;

    this.setState({
      charsLeft: charsLeftIsNegative ? 0 : charsLeft,
      linesLeft: linesLeftIsNegative ? 0 : maxLines - linesArray.length,
    });
  }


  render() {
    const {
      ariaDescribedBy,
      describedById,
      maxChars,
      maxLines,
      value,
      ...rest
    } = this.props;

    const {
      charsLeft,
      linesLeft,
    } = this.state;

    // need to declare this here to get around jsx-ally linting issue
    const role = 'text';

    return (
      <div className={styles.messageContainer}>
        <Textarea
          {...rest}
          ref={(el) => {
            this.textArea = el;
          }}
          ariaDescribedBy={ariaDescribedBy}
          defaultValue={value}
          extensionProps={{
            onChange: this.onChange,
            onKeyDown: this.onKeyDown,
            onKeyUp: this.onKeyUp,
            onFocus: this.onFocus,
            onClick: this.onFocus,
            onBlur: this.onBlur,
            onPaste: this.onPaste,
          }}
          maxLength={maxChars + (maxLines - 1)}
        />
        <div id={describedById}>
          <div className={styles.stats} role={role}>
            <strong>{charsLeft}</strong> characters and <strong>{linesLeft}</strong> lines remaining
          </div>
          <div className={styles.instruction}>This message will appear on the delivery note</div>
        </div>
      </div>
    );
  }
}

export default GiftMessage;
